import React, { FC, useState, useEffect, ReactNode } from "react";
import Button, { Variant } from "@components/buttons/Button/Button";
import Card from "@components/cards/Card/Card";
import { Link } from "react-router-dom";
import useMediaQuery from "@hooks/use-media-query";
import styles from "./InfoCard.module.css";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader"; // Assuming you have a skeleton loader
import Lightbox from "../Lightbox/Lightbox";

interface ButtonConfig {
    title: ReactNode;
    variant: Variant;
    onClick?: () => void;
    to?: string; // Internal route navigation using React Router
    href?: string; // External link
    newTab?: boolean;
    disabled?: boolean;
}

interface MediaItemType {
    type: "image" | "video";
    src: string;
}

interface InfoCardProps {
    title: string;
    badge?: { label: string, tone?: "default" | "accent" }; // Small label shown beside the title
    items?: string[];
    media?: MediaItemType;
    buttons?: ButtonConfig[];
    bottomButtons?: ButtonConfig[];
    bottomText?: string;
    bottomTextLink?: string;
    coverMedia?: boolean;
}

const InfoCard: FC<InfoCardProps> = ({
    media,
    title,
    badge,
    items,
    buttons,
    bottomButtons,
    bottomText,
    bottomTextLink,
    coverMedia,
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [videoPoster, setVideoPoster] = useState<string | null>(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxMedia, setLightboxMedia] = useState<MediaItemType | null>(null);

    const isMobile = useMediaQuery("(max-width: 768px)");

    const alt = typeof title === "string" ? title : undefined;

    useEffect(() => {
        if (media?.type === "image") {
            const img = new Image();
            img.src = media.src;
            img.onload = () => {
                if (!isLoaded) {
                    setIsLoaded(true);
                }
            };
            img.onerror = () => console.error(`Image failed to load: ${media.src}`);
        } else if (media?.type === "video") {
            const video = document.createElement("video");
            video.src = media.src;
            video.preload = "metadata";
            video.crossOrigin = "anonymous";
            video.onloadedmetadata = () => {
                video.currentTime = 1;
            };
            video.onseeked = () => {
                if (!isLoaded) {
                    const canvas = document.createElement("canvas");
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const posterUrl = canvas.toDataURL("image/jpeg");
                        setVideoPoster(posterUrl);
                        setIsLoaded(true);
                    }
                }
            };
            video.onerror = () => console.error(`Video failed to load: ${media.src}`);
        }
    }, [isLoaded, media]);

    const handleMediaClick = () => {
        if (media) {
            setLightboxMedia(media);
            setIsLightboxOpen(true);
        }
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
        setLightboxMedia(null);
    };

    return (
        <Card className={styles.infoCard}>
            {media && (
                <div className={styles.mediaContainer}>
                    {(media.src && !isLoaded) && <SkeletonLoader />}
                    {(isLoaded || !media.src) && (
                        media.type === "image" ? (
                            <img
                                src={media.src || "/assets/images/placeholder-media.svg"}
                                alt={alt}
                                className={`${styles.media} ${coverMedia ? styles.cover : styles.contain}`}
                                loading="lazy"
                                onClick={handleMediaClick}
                            />
                        ) : (
                            <div
                                className={styles.videoContainer}
                                onClick={handleMediaClick} // Handle video play on click
                            >
                                <video
                                    className={`${videoPoster ? styles.visible : styles.hidden} ${coverMedia ? styles.cover : styles.contain}`}
                                    poster={videoPoster || undefined}
                                    preload="none"
                                />
                                <div className={styles.playIcon} />
                            </div>
                        )
                    )}
                </div>
            )}
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        {title}
                        {badge && (
                            <span className={`${styles.badge} ${badge.tone === "accent" ? styles.badgeAccent : ""}`}>{badge.label}</span>
                        )}
                    </div>
                    {buttons && buttons.length > 0 && (
                        <div className={styles.buttons}>
                            {buttons.map((button, index) => (
                                <Button
                                    key={index}
                                    onClick={button.onClick}
                                    to={button.to}
                                    href={button.href}
                                    target={button.newTab ? "_blank" : "_self"}
                                    variant={button.variant}
                                    size={isMobile ? "small" : "medium"}
                                    disabled={button.disabled}
                                >
                                    {button.title}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
                {items && items.length > 0 && (
                    <div className={styles.items}>
                        {items.map((item, index) => (
                            <div key={index} className={styles.item}>
                                {item}
                            </div>
                        ))}
                    </div>
                )}
                {(bottomButtons || bottomText) && (
                    <div className={styles.bottomSection}>
                        {bottomButtons && bottomButtons.length > 0 && (
                            <div className={styles.bottomButtons}>
                                {bottomButtons.map((button, index) => (
                                    <Button
                                        key={index}
                                        onClick={button.onClick}
                                        to={button.to}
                                        href={button.href}
                                        target={button.newTab ? "_blank" : "_self"}
                                        variant={button.variant}
                                        size={isMobile ? "small" : "medium"}
                                        disabled={button.disabled}
                                    >
                                        {button.title}
                                    </Button>
                                ))}
                            </div>
                        )}
                        {bottomText && bottomTextLink ? (
                            <Link to={bottomTextLink} className={styles.bottomText}>
                                {bottomText}
                            </Link>
                        ) : (
                            <div className={styles.bottomText}>{bottomText}</div>
                        )}
                    </div>
                )}
            </div>
            {isLightboxOpen && lightboxMedia && (
                <Lightbox media={lightboxMedia} onClose={closeLightbox} />
            )}
        </Card>
    );
};

export default InfoCard;
